import { useCallback, useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import api from '../../api/api';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import CodeHierarchyBrowser from './CodeHierarchyBrowser';
import CodeLookupDetail from './CodeLookupDetail';
import '../../styles/editcase/CodeSearchModal.css';

// Search + hierarchy-browse modal for the Coding step. Two modes via
// `target`: {mode: 'refine', code, index} opens the hierarchy browser at
// the AI-suggested code's neighborhood; {mode: 'lookup'} opens on an empty
// search box and inspects codes without touching the case's code list —
// picking a hit opens an inspector pane with an on-demand document
// evidence check (onLookupEvidence/lookupEvidence, keyed by code) and an
// explicit "Add to case" action. Local state only — the parent persists
// the selection through onSelect(hit) where hit carries the table facts
// needed to build the candidate client-side. `asOf` (ISO date, the case
// creation date) selects the fiscal-year table; absent means today.
export default function CodeSearchModal({
    target,
    onSelect,
    onClose,
    onLookupEvidence,
    lookupEvidence = {},
    caseCodes,
    asOf,
}) {
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [browseCode, setBrowseCode] = useState(
        target.mode === 'refine' ? target.code : null
    );
    const [selecting, setSelecting] = useState(false);
    const [inspectedHit, setInspectedHit] = useState(null);
    const isLookup = target.mode === 'lookup';
    const debouncedQuery = useDebouncedValue(query, 250);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        if (debouncedQuery.trim().length < 2) {
            setHits([]);
            setSearchError(null);
            return undefined;
        }
        let cancelled = false;
        setSearching(true);
        const params = { q: debouncedQuery, limit: 20 };
        if (asOf) params.as_of = asOf;
        api.get('/icd/search', { params })
            .then(resp => {
                if (!cancelled) {
                    setHits(resp.data.hits);
                    setSearchError(null);
                }
            })
            .catch(() => {
                if (!cancelled) setSearchError('Search failed. Try again.');
            })
            .finally(() => {
                if (!cancelled) setSearching(false);
            });
        return () => { cancelled = true; };
    }, [debouncedQuery, asOf]);

    // Resolve a bare code (from the hierarchy browser) into a full hit with
    // description + PDGM facts so the parent can stamp the candidate without
    // another roundtrip.
    const selectByCode = useCallback((code) => {
        setSelecting(true);
        const url = `/icd/code/${encodeURIComponent(code)}`;
        const request = asOf ? api.get(url, { params: { as_of: asOf } }) : api.get(url);
        request
            .then(resp => {
                const { code: node, pdgm, ancestors } = resp.data;
                const hit = {
                    code: node.code,
                    description: node.description,
                    billable: node.billable,
                    pdgm,
                    ancestors,
                };
                if (isLookup) {
                    // Inspect instead of selecting; the modal stays open.
                    setInspectedHit(hit);
                    setSelecting(false);
                } else {
                    onSelect(hit);
                }
            })
            .catch(() => {
                setSelecting(false);
                setSearchError('Could not load that code. Try again.');
            });
    }, [onSelect, isLookup, asOf]);

    const title = target.mode === 'refine'
        ? `Refine ${target.code}`
        : 'Look up a code';

    return (
        <div className="code-search-overlay" onClick={onClose}>
            <div
                className="code-search-modal"
                role="dialog"
                aria-label={title}
                onClick={event => event.stopPropagation()}
            >
                <div className="code-search-header">
                    <span className="code-search-title">{title}</span>
                    <button className="code-search-close" onClick={onClose} aria-label="Close">
                        &#10005;
                    </button>
                </div>

                <input
                    className="code-search-input"
                    type="text"
                    placeholder="Search by condition, code, or phrase (e.g. diastolic heart failure)"
                    value={query}
                    onChange={event => {
                        setQuery(event.target.value);
                        setBrowseCode(null);
                        setInspectedHit(null);
                    }}
                    autoFocus
                />

                {selecting && (
                    <div className="code-search-status">
                        <ClipLoader size={14} /> {isLookup ? 'Loading code' : 'Adding code'}&hellip;
                    </div>
                )}
                {searchError && <div className="code-search-error">{searchError}</div>}

                {isLookup && inspectedHit ? (
                    <CodeLookupDetail
                        hit={inspectedHit}
                        result={lookupEvidence[inspectedHit.code]}
                        onList={caseCodes?.has(inspectedHit.code)}
                        backLabel={browseCode ? 'Back to hierarchy' : 'Back to results'}
                        onBack={() => setInspectedHit(null)}
                        onCheckEvidence={() => onLookupEvidence(inspectedHit.code)}
                        onAdd={() => onSelect(inspectedHit)}
                        onBrowse={() => {
                            setBrowseCode(inspectedHit.code);
                            setInspectedHit(null);
                        }}
                    />
                ) : browseCode ? (
                    <CodeHierarchyBrowser
                        code={browseCode}
                        onNavigate={setBrowseCode}
                        onSelect={selectByCode}
                        asOf={asOf}
                    />
                ) : (
                    <div className="code-search-results">
                        {searching && (
                            <div className="code-search-status"><ClipLoader size={14} /> Searching&hellip;</div>
                        )}
                        {!searching && debouncedQuery.trim().length >= 2 && hits.length === 0 && !searchError && (
                            <div className="code-search-status">
                                No codes match &ldquo;{debouncedQuery}&rdquo;. Try a shorter
                                phrase or the code itself.
                            </div>
                        )}
                        {hits.map(hit => (
                            <div key={hit.code} className="code-search-hit">
                                <div className="code-search-hit-main">
                                    <span className="code-search-hit-code">{hit.code}</span>
                                    <span className="code-search-hit-desc">{hit.description}</span>
                                    <span className={`code-badge ${hit.billable ? 'code-badge--billable' : 'code-badge--header'}`}>
                                        {hit.billable ? 'Billable' : 'Header'}
                                    </span>
                                </div>
                                {hit.ancestors.length > 0 && (
                                    <div className="code-search-hit-crumb">
                                        {hit.ancestors.map(a => a.description).join(' › ')}
                                    </div>
                                )}
                                {hit.term_path && (
                                    <div className="code-search-hit-path">Index: {hit.term_path}</div>
                                )}
                                {hit.pdgm?.comorbidity_subgroup && (
                                    <span className="dx-comorbidity-chip">
                                        PDGM comorbidity: {hit.pdgm.comorbidity_subgroup}
                                    </span>
                                )}
                                <div className="code-search-hit-actions">
                                    {(hit.has_children || !hit.billable) && (
                                        <button
                                            className="code-browser-btn"
                                            onClick={() => setBrowseCode(hit.code)}
                                        >
                                            Browse
                                        </button>
                                    )}
                                    {hit.billable ? (
                                        <button
                                            className="code-browser-btn code-browser-btn--select"
                                            onClick={() => (isLookup ? setInspectedHit(hit) : onSelect(hit))}
                                        >
                                            {isLookup ? 'Details' : 'Use this code'}
                                        </button>
                                    ) : (
                                        <span className="code-search-hit-hint">
                                            Choose a specific code inside
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </div>
        </div>
    );
}
